import { type ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useI18n } from '../lib/i18n';
import { Button, Card, ErrorState, LoadingBlock, PageHeader } from './ui';

/**
 * Every form in the portal is a page, not a popup — the same rule as the admin,
 * which this shell was copied from.
 *
 * This is the shared shell so they all behave the same: a back link, a title,
 * the fields in a card, and one action bar that sticks to the bottom of the
 * viewport on a long form. Cancel returns the way the user came rather than to
 * a fixed route, so a form reached from a list goes back to that list and one
 * reached from a detail page goes back to that record.
 */
export function FormPage({
  title,
  subtitle,
  backTo,
  backLabel,
  summary,
  children,
  submitLabel,
  onSubmit,
  submitting = false,
  disabled = false,
  destructive = false,
  loading = false,
  error,
  onRetry,
  width = 'md',
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Where the back link and Cancel go. Defaults to one step back in history. */
  backTo?: string;
  backLabel: string;
  /** Read-only context shown above the inputs — CR-001 §48 wants this on payments. */
  summary?: ReactNode;
  children: ReactNode;
  submitLabel: string;
  onSubmit: () => void;
  submitting?: boolean;
  disabled?: boolean;
  destructive?: boolean;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  width?: 'md' | 'lg' | 'full';
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const goBack = () => (backTo ? navigate(backTo) : navigate(-1));

  const widths = { md: 'max-w-3xl', lg: 'max-w-5xl', full: '' };

  return (
    <div className={widths[width]}>
      <div className="mb-4">
        {backTo ? (
          <Link
            to={backTo}
            className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]"
          >
            <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
            {backLabel}
          </Link>
        ) : (
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]"
          >
            <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
            {backLabel}
          </button>
        )}
      </div>

      <PageHeader title={title} subtitle={subtitle} />

      {loading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={onRetry} />
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!disabled && !submitting) onSubmit();
          }}
        >
          {summary && (
            <Card className="mb-5 bg-[var(--surface-sunken)]">{summary}</Card>
          )}

          <Card>{children}</Card>

          {/* Sticky so the action bar is reachable without scrolling a long form
              back to the top, and so it never hides behind the page padding. */}
          <div className="sticky bottom-0 z-sticky mt-5 -mx-4 border-t hairline bg-[var(--surface-page)]/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6">
            <div className="flex justify-end gap-2">
              <Button type="button" onClick={goBack}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                variant={destructive ? 'danger' : 'primary'}
                loading={submitting}
                disabled={disabled}
              >
                {submitLabel}
              </Button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}

/** The usual two-column field grid inside a `FormPage`. */
export function FormGrid({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`grid gap-4 sm:grid-cols-2 ${className}`}>{children}</div>;
}
